<template>
  <section class="introduction-section">
    <div v-if="contentSections.length" class="content-section-list">
      <article
        v-for="(section, index) in contentSections"
        :key="`${section.title}-${index}`"
        class="content-section"
        :class="{ reverse: index % 2 === 1 }"
      >
        <div v-if="section.images.length" class="content-image-list">
          <button
            v-for="(image, imageIndex) in section.images"
            :key="`${image}-${imageIndex}`"
            type="button"
            class="content-image-wrap"
            :aria-label="`${section.title || imageAlt} ${imageIndex + 1}－點擊放大圖片`"
            @click="openLightbox(image, `${section.title || imageAlt} ${imageIndex + 1}`)"
          >
            <img
              :src="image"
              :alt="`${section.title || imageAlt} ${imageIndex + 1}`"
              class="content-image"
              loading="lazy"
            />
            <span class="content-image-hint" aria-hidden="true">點擊放大</span>
          </button>
        </div>

        <div class="content-copy">
          <span class="content-number">
            {{ String(index + 1).padStart(2, "0") }}
          </span>

          <h2 v-if="section.title" v-html="formatInline(section.title)"></h2>

          <div v-if="section.paragraphs.length" class="content-paragraphs">
            <template
              v-for="(paragraph, paragraphIndex) in section.paragraphs"
              :key="paragraphIndex"
            >
              <template
                v-for="(block, blockIndex) in getRichBlocks(paragraph)"
                :key="blockIndex"
              >
                <h3
                  v-if="block.type === 'h1'"
                  class="rich-heading rich-heading-large"
                  v-html="formatInline(block.text)"
                ></h3>

                <h4
                  v-else-if="block.type === 'h2'"
                  class="rich-heading rich-heading-medium"
                  v-html="formatInline(block.text)"
                ></h4>

                <h5
                  v-else-if="block.type === 'h3'"
                  class="rich-heading rich-heading-small"
                  v-html="formatInline(block.text)"
                ></h5>

                <ul v-else-if="block.type === 'list'" class="content-list">
                  <li
                    v-for="(item, itemIndex) in block.items"
                    :key="itemIndex"
                    v-html="formatInline(item)"
                  ></li>
                </ul>

                <p v-else v-html="formatInline(block.text)"></p>
              </template>
            </template>
          </div>
        </div>
      </article>
    </div>

    <div v-else-if="fallbackImages.length" class="fallback-image-grid">
      <button
        v-for="(image, index) in fallbackImages"
        :key="`${image}-${index}`"
        type="button"
        class="fallback-image"
        :aria-label="`${imageAlt} ${index + 1}－點擊放大圖片`"
        @click="openLightbox(image, `${imageAlt} ${index + 1}`)"
      >
        <img :src="image" :alt="`${imageAlt} ${index + 1}`" loading="lazy" />
      </button>
    </div>

    <div v-else class="empty-state">
      {{ emptyText }}
    </div>

    <Teleport to="body">
      <Transition name="lightbox-fade">
        <div
          v-if="lightboxImage"
          class="image-lightbox"
          role="dialog"
          aria-modal="true"
          :aria-label="lightboxAlt"
          @click.self="closeLightbox"
        >
      <div class="image-lightbox-panel">
          <button
            type="button"
            class="lightbox-close"
            aria-label="關閉圖片"
            @click="closeLightbox"
          >
            ×
          </button>

          <img :src="lightboxImage" :alt="lightboxAlt" class="image-lightbox-content" />
        
      </div>
    </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

export default {
  name: "IntroductionSection",

  props: {
    content: {
      type: String,
      default: "",
    },

    images: {
      type: Array,
      default: () => [],
    },

    imageAlt: {
      type: String,
      default: "",
    },

    emptyText: {
      type: String,
      default: "目前尚無書籍介紹。",
    },
  },

  setup(props) {
    const lightboxImage = ref("");
    const lightboxAlt = ref("");

    const resolveImageUrl = (imageUrl) => {
      const value = String(imageUrl || "").trim();
      if (!value) return "";

      if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
        return value;
      }

      return `${process.env.BASE_URL}${value.replace(/^\//, "")}`;
    };

    const openLightbox = (image, alt = "") => {
      lightboxImage.value = image;
      lightboxAlt.value = alt;
      document.body.style.overflow = "hidden";
    };

    const closeLightbox = () => {
      lightboxImage.value = "";
      lightboxAlt.value = "";
      document.body.style.overflow = "";
    };

    const handleKeydown = (event) => {
      if (event.key === "Escape" && lightboxImage.value) {
        closeLightbox();
      }
    };

    onMounted(() => {
      window.addEventListener("keydown", handleKeydown);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", handleKeydown);
      document.body.style.overflow = "";
    });

    const escapeHtml = (value) =>
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    // Google Sheet 支援：
    // **粗體**、*斜體*、{red}紅色{/red}、{big}大字{/big}、{mark}螢光標示{/mark}
    const formatInline = (value) => {
      let html = escapeHtml(value);

      const richTags = {
        red: "rich-red",
        blue: "rich-blue",
        green: "rich-green",
        orange: "rich-orange",
        yellow: "rich-yellow",
        purple: "rich-purple",
        gray: "rich-gray",
        big: "rich-big",
        small: "rich-small",
        mark: "rich-mark",
      };

      Object.entries(richTags).forEach(([tag, className]) => {
        const pattern = new RegExp(
          `\\{${tag}\\}([\\s\\S]+?)\\{\\/${tag}\\}`,
          "gi",
        );

        html = html.replace(pattern, `<span class="${className}">$1</span>`);
      });

      html = html.replace(/\*\*\s*([^*]+?)\s*\*\*/g, "<strong>$1</strong>");
      html = html.replace(/(^|[^*])\*([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");

      return html;
    };
    const normalizeContentText = (value) =>
      String(value || "")
        // Google Sheet / API 有時會把換行傳成字面上的 \n，先還原成真正換行
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\r\n?/g, "\n")
        // 清掉不容易察覺、但可能影響解析的空白字元
        .replace(/\u00a0/g, " ")
        .replace(/[\u200B-\u200D\uFEFF]/g, "");

    const getRichBlocks = (paragraph) => {
      const lines = normalizeContentText(paragraph)
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const blocks = [];
      let listItems = [];

      const pushList = () => {
        if (!listItems.length) return;

        blocks.push({
          type: "list",
          items: [...listItems],
        });

        listItems = [];
      };

      lines.forEach((line) => {
        // 清單
        const listMatch = line.match(/^(?:[•●▪◦-]|\d+[.)])\s+(.+)$/);

        if (listMatch) {
          listItems.push(listMatch[1].trim());
          return;
        }

        pushList();

        // Markdown 標題
        const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);

        if (headingMatch) {
          blocks.push({
            type: `h${headingMatch[1].length}`,
            text: headingMatch[2].trim(),
          });

          return;
        }

        // 一般文字
        blocks.push({
          type: "paragraph",
          text: line,
        });
      });

      pushList();

      return blocks;
    };

    const parseContentSections = (value) => {
      const text = normalizeContentText(value).trim();

      if (!text) return [];

      return text
        .split(/^\s*(?:-{20,}|={6,})\s*$/gm)
        .map((block) => block.trim())
        .filter(Boolean)
        .map((block) => {
          const titleMatch = block.match(/【([^】]+)】/);
          const imageMatch = block.match(/(?:圖片|Image)\s*[：:]\s*([^\n]+)/i);

          const title = titleMatch?.[1]?.trim() || "";
          const images = (imageMatch?.[1] || "")
            .split(",")
            .map((image) => resolveImageUrl(image))
            .filter(Boolean);

          const content = block
            .replace(/【[^】]+】/, "")
            .replace(/(?:圖片|Image)\s*[：:]\s*[^\n]+/i, "")
            .trim();

          const paragraphs = content
            .split(/\n\s*\n/)
            .map((paragraph) =>
              paragraph
                .split(/\n/)
                .map((line) => line.trim())
                .filter(Boolean)
                .join("\n"),
            )
            .filter(Boolean);

          return {
            title,
            images,
            paragraphs,
          };
        })
        .filter(
          (section) =>
            section.title || section.images.length || section.paragraphs.length,
        );
    };

    const contentSections = computed(() => {
      const sections = parseContentSections(props.content);

      return sections.map((section, index) => {
        const fallbackImage = resolveImageUrl(props.images[index]);

        return {
          ...section,
          images: section.images.length
            ? section.images
            : fallbackImage
              ? [fallbackImage]
              : [],
        };
      });
    });

    const fallbackImages = computed(() =>
      props.images.map(resolveImageUrl).filter(Boolean),
    );

    return {
      contentSections,
      fallbackImages,
      lightboxImage,
      lightboxAlt,
      openLightbox,
      closeLightbox,
      getRichBlocks,
      formatInline,
    };
  },
};
</script>

<style scoped>
.introduction-section {
  width: min(100%, 1040px);
  /* margin: 82px auto 0; */
}

.content-section-list {
  display: grid;
}

.content-section {
  display: grid;
  grid-template-columns: minmax(0, 430px) minmax(0, 430px);
  justify-content: center;
  align-items: center;
  gap: 18px;
  min-height: 330px;
  padding: 42px 0;
  border-bottom: 1px solid #e3e9ee;
}

.content-section:first-child {
  padding-top: 8px;
}

.content-section:last-child {
  border-bottom: 0;
}

.content-section.reverse .content-image-list {
  order: 2;
}

.content-section.reverse .content-copy {
  order: 1;
}

.content-image-list {
  display: flex;
  width: min(100%, 400px);
  flex-direction: column;
  gap: 0;
  justify-self: center;
}

.content-image-wrap {
  position: relative;
  display: grid;
  width: min(100%, 400px);
  height: 260px;
  overflow: hidden;
  justify-self: center;
  place-items: center;
  border: 0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(28, 64, 91, 0.1);
  cursor: zoom-in;
  appearance: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.content-image-wrap:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 44px rgba(28, 64, 91, 0.16);
}

.content-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.content-image-hint {
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(23, 63, 95, 0.84);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
}

.content-copy {
  display: flex;
  min-height: 220px;
  flex-direction: column;
  justify-content: center;
}

.content-number {
  display: block;
  margin-bottom: 12px;
  color: #24618d;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-align: left;
}

.content-copy h2 {
  margin: 0 0 16px;
  color: #173f5f;
  /* font-size: clamp(24px, 2.6vw, 34px); */
  font-size: 30px;
  line-height: 1.25;
  text-align: left;
}

.content-paragraphs {
  max-width: 420px;
  color: #556573;
  font-size: 15px;
  line-height: 1.85;
}

.content-paragraphs p {
  margin: 0 0 14px;
  text-align: left;
}

.content-paragraphs p:last-child {
  margin-bottom: 0;
}

.content-list {
  margin: 0;
  padding-left: 22px;
  color: #556573;
  text-align: left;
}

.content-list li {
  margin-bottom: 7px;
  padding-left: 2px;
  line-height: 1.75;
}

.content-list li:last-child {
  margin-bottom: 0;
}

.rich-heading {
  margin: 18px 0 10px;
  color: inherit;
  font-weight: 750;
  line-height: 1.4;
  text-align: left;
}

.rich-heading-large {
  font-size: 24px;
}
.rich-heading-medium {
  font-size: 20px;
}
.rich-heading-small {
  font-size: 17px;
}

:deep(.rich-red) {
  color: #8f1d2c;
}
:deep(.rich-blue) {
  color: #1565c0;
}
:deep(.rich-green) {
  color: #2e7d32;
}
:deep(.rich-orange) {
  color: #d95f02;
}
:deep(.rich-yellow) {
  color: #c58a00;
}
:deep(.rich-purple) {
  color: #6a1b9a;
}
:deep(.rich-gray) {
  color: #757575;
}

:deep(.rich-big) {
  font-size: 1.25em;
  font-weight: 700;
}

:deep(.rich-small) {
  font-size: 0.82em;
}

:deep(.rich-mark) {
  padding: 0.08em 0.25em;
  border-radius: 0.2em;
  background: #fff1a8;
  color: inherit;
}

:deep(strong) {
  color: inherit;
  font-weight: 750;
}

:deep(em) {
  color: inherit;
  font-style: italic;
}

@media (max-width: 900px) {
  .content-section {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .content-section.reverse .content-image-list,
  .content-section.reverse .content-copy {
    order: initial;
  }

  .content-copy {
    width: min(100%, 680px);
    min-height: 0;
    margin: 0 auto;
  }
}

@media (max-width: 600px) {
  .introduction-section {
    margin-top: 58px;
  }

  .content-section {
    padding: 30px 0;
  }

  .content-image-wrap {
    width: min(100%, 340px);
    height: 230px;
  }

  .content-image-hint {
    display: none;
  }

  .fallback-image-grid {
    grid-template-columns: 1fr;
  }

  .image-lightbox {
    padding: 18px;
  }
}


.lightbox-close {
  position: absolute;
  z-index: 100000;
  top: -18px;
  right: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(23, 79, 125, 0.12);
  border-radius: 50%;
  background: #fff;
  color: #174f7d;
  font-family: Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32);
}

.lightbox-close:hover {
  background: #f1f6f9;
  transform: scale(1.05);
}

.lightbox-close:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.9);
  outline-offset: 3px;
}



.image-lightbox {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(0, 0, 0, 0.78);
}


.image-lightbox-panel {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 92vw;
  max-height: 88vh;
}


.image-lightbox-content {
  display: block;
  width: auto;
  max-width: 92vw;
  max-height: 88vh;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
}
</style>
